import { Collection, CollectionProduct } from 'types/collection'
import { isMatchingVariantForOptions } from './isMatchingVariantForOptions'
import { FilterOption } from 'types/filters'

type CollectionProductsListWithMatchingVariantsArgs = {
  product: CollectionProduct
  productsWithMatchingVariants: CollectionProduct[]
  filterOptions: FilterOption[]
  variantExist: boolean
  facetTypes: Set<string>
}

function getCollectionProductsListWithMatchingVariants({
  product,
  productsWithMatchingVariants,
  filterOptions,
  variantExist,
  facetTypes
}: CollectionProductsListWithMatchingVariantsArgs) {
  const selectedFacetsByType = filterOptions.reduce<Record<string, string[]>>(
    (selectedFacets, filter) => {
      if (!facetTypes.has(filter.type)) return selectedFacets

      return {
        ...selectedFacets,
        [filter.type]: [...(selectedFacets[filter.type] || []), filter.value]
      }
    },
    {}
  )
  const matchesFacets = Object.entries(selectedFacetsByType).every(
    ([type, selectedValues]) =>
      product.facets?.some(
        facet =>
          facet.type === type &&
          selectedValues.some(value => facet.values.includes(value))
      )
  )

  if (!matchesFacets) return productsWithMatchingVariants

  const variantFilterOptions = filterOptions.filter(
    filter => !facetTypes.has(filter.type)
  )
  const atLeastOneMatch = (product.variants || []).some(variant => {
    return (
      variant.inventory_quantity &&
      isMatchingVariantForOptions({
        productOptions: product.options,
        variant,
        filterOptions: variantFilterOptions
      })
    )
  })

  return atLeastOneMatch ||
    !variantExist ||
    /Belts|Socks/.test(product.product_type)
    ? [...productsWithMatchingVariants, product]
    : productsWithMatchingVariants
}

type CollectionProductData = {
  collections: Collection[]
  filterOptions: FilterOption[]
  variantExist: boolean
}

export default function getCollectionProductData({
  collections,
  filterOptions,
  variantExist
}: CollectionProductData) {
  const collectionsWithFilteredProducts: Collection[] = []
  const facetTypes = new Set(
    collections.flatMap(collection =>
      collection.items.flatMap(product =>
        (product.facets || []).map(facet => facet.type)
      )
    )
  )
  const totalResults = collections.reduce((total, collection) => {
    const collectionProducts = collection.items.reduce(
      (
        productsWithMatchingVariants: CollectionProduct[],
        product: CollectionProduct
      ) =>
        getCollectionProductsListWithMatchingVariants({
          product,
          productsWithMatchingVariants,
          filterOptions,
          variantExist,
          facetTypes
        }),
      []
    )
    if (collectionProducts.length > 0) {
      collectionsWithFilteredProducts.push({
        ...collection,
        items: collectionProducts
      })
      return total + collectionProducts.length
    }
    return total
  }, 0)
  return { collectionsWithFilteredProducts, totalResults }
}
